import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Lock, ArrowRight } from 'lucide-react'
import { Group, Skeleton } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'

import {
  listPromptChanges,
  listPrompts,
  publishPrompt,
  type PromptChange,
  type PromptSummaryDTO,
  type PromptInject,
} from '@/services/promptAdminService'
import { useAdminScope } from '../-admin/adminScope'
import { PublishBar, PublishDialog, type PublishChangeItem } from '../-admin/PublishBar'
import { PromptEditor } from '../-prompts/PromptEditor'
import { PipelineGraph } from '../-prompts/PipelineGraph'
import { PageHeader } from '@/components/page/PageHeader'
import '@/styles/pages.css'

export const Route = createFileRoute('/_app/organization/admin/prompts')({
  head: () => ({ meta: [{ title: 'Prompts · Admin · AERRIS' }] }),
  component: PromptsTab,
})

/** risk_policy is edited (and published, with its re-evaluation opt-in) on the Risk tab. */
const RISK_POLICY = 'risk_policy'
const DISPLAY_VOCABULARY = 'display_vocabulary'

const CHANGE_DETAIL: Record<PromptChange['kind'], string> = {
  edited: 'Edited',
  reset: 'Reset to shipped',
}

function PromptsTab() {
  const { customerUuid, globalMode, clientId, canManage, accessLevel, scopeName, loading: scopeLoading } = useAdminScope()
  const queryClient = useQueryClient()

  const [prompts, setPrompts] = useState<PromptSummaryDTO[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [promptsLoading, setPromptsLoading] = useState(true)
  // Null = the changes could not be read (the bar still offers a publish of nothing itemised).
  const [changes, setChanges] = useState<PromptChange[] | null>([])
  const [publishOpen, setPublishOpen] = useState(false)
  const [publishing, setPublishing] = useState(false)
  // Bumped after a publish from the bar, so the open editor re-reads its prompt.
  const [editorKey, setEditorKey] = useState(0)

  const inScope = canManage && (globalMode || Boolean(clientId))

  const fetchAll = useCallback(async () => {
    // risk_policy is edited on the Risk tab (with the baseline it belongs beside) — hide it
    // here so there is exactly one editing surface for it.
    const list = (await listPrompts(customerUuid)).filter((p) => p.name !== RISK_POLICY)
    // Group order follows the pipeline (stepOrder), then alphabetical within a step.
    const sorted = [...list].sort(
      (a, b) => (a.stepOrder ?? 99) - (b.stepOrder ?? 99) || a.name.localeCompare(b.name),
    )
    setPrompts(sorted)
    setSelected((prev) => (prev && sorted.some((p) => p.name === prev) ? prev : (sorted[0]?.name ?? null)))
  }, [customerUuid])

  const loadChanges = useCallback(async () => {
    try {
      const list = await listPromptChanges(customerUuid)
      setChanges(Array.isArray(list) ? list.filter((c) => c.name !== RISK_POLICY) : [])
    } catch (e) {
      console.error('Failed to load prompt changes:', e)
      setChanges(null)
    }
  }, [customerUuid])

  const load = useCallback(async () => {
    if (scopeLoading) return
    setPromptsLoading(true)
    try {
      if (!inScope) {
        setPrompts([])
        setSelected(null)
        setChanges([])
        return
      }
      await fetchAll()
      void loadChanges()
    } catch (e) {
      console.error('Failed to load prompts:', e)
      notifications.show({ color: 'red', title: 'Could not load prompts', message: 'Please try again.' })
    } finally {
      setPromptsLoading(false)
    }
  }, [inScope, scopeLoading, fetchAll, loadChanges])

  // After the editor writes: re-read the list and the changes without unmounting the editor.
  const refresh = useCallback(async () => {
    try {
      await fetchAll()
    } catch (e) {
      console.error('Failed to reload prompts:', e)
    }
    await loadChanges()
  }, [fetchAll, loadChanges])

  useEffect(() => {
    void load()
  }, [load])

  /** "Review and publish": publish every changed prompt in turn; one refusal does not stop the rest. */
  const handlePublishAll = async (note: string) => {
    if (!changes || publishing) return
    setPublishing(true)
    const published: string[] = []
    const failed: string[] = []
    for (const c of changes) {
      try {
        await publishPrompt(customerUuid, c.name, note || undefined)
        published.push(c.name)
      } catch (e) {
        console.error(`Publish of ${c.name} failed:`, e)
        failed.push(c.name)
      }
    }
    if (published.includes(DISPLAY_VOCABULARY)) {
      await queryClient.invalidateQueries({ queryKey: ['displayVocabulary'] })
    }
    setPublishing(false)
    setPublishOpen(false)
    setEditorKey((k) => k + 1)
    await refresh()
    if (published.length) {
      notifications.show({
        color: 'green',
        title: `Published ${published.length} ${published.length === 1 ? 'prompt' : 'prompts'}`,
        message: `${published.join(', ')} ${published.length === 1 ? 'is' : 'are'} now live for extraction.`,
      })
    }
    if (failed.length) {
      notifications.show({
        color: 'red',
        title: 'Publish blocked',
        message: `${failed.join(', ')}: a placeholder or dry-run validation check failed, or you lack admin access.`,
      })
    }
  }

  const changeItems: PublishChangeItem[] = (changes ?? []).map((c) => ({
    key: c.name,
    label: c.name,
    detail: CHANGE_DETAIL[c.kind] ?? c.kind,
  }))

  const selectedInjects = useMemo(
    () => prompts.find((p) => p.name === selected)?.injects ?? [],
    [prompts, selected],
  )

  const loading = scopeLoading || promptsLoading

  const header = (
    <PageHeader
      title="Prompts"
      subtitle="The extraction pipeline. Extract runs once per contract, Enrich runs once per clause, and management-accounts files take a separate path. Select a step to edit its prompt."
    />
  )

  if (loading) {
    return (
      <div className="prompts-page">
        {header}
        <Group align="stretch" gap="md" wrap="nowrap" aria-busy="true">
          <Skeleton height={180} style={{ flex: 1 }} radius="md" />
          <Skeleton height={180} style={{ flex: 1 }} radius="md" />
          <Skeleton height={180} style={{ flex: 1 }} radius="md" />
        </Group>
        <Skeleton height={320} radius="md" />
      </div>
    )
  }

  if (!canManage) {
    return (
      <div className="prompts-page">
      {header}
      <div className="notice">
        <Lock size={16} />
        {globalMode
          ? 'Editing the global default prompts requires platform-admin access.'
          : !clientId
            ? 'Select a client to manage its prompts.'
            : `You need admin access to ${scopeName} to edit its prompts${accessLevel ? ` (your access: ${accessLevel})` : ''}.`}
      </div>
      </div>
    )
  }

  if (prompts.length === 0) {
    return (
      <div className="prompts-page">
        {header}
        <div className="notice">No prompts found for {scopeName}.</div>
      </div>
    )
  }

  return (
    <div className="prompts-page">
      {header}
      <nav aria-label="Extraction pipeline">
        <PipelineGraph prompts={prompts} selected={selected} onSelect={setSelected} />
      </nav>
      <section className="page-card prompts-editor-card" aria-label="Prompt editor">
        {selectedInjects.length > 0 && <InjectionPanel injects={selectedInjects} />}
        {selected && (
          <PromptEditor
            customerUuid={customerUuid}
            name={selected}
            key={`${customerUuid}:${selected}:${editorKey}`}
            canManage={canManage}
            onChanged={refresh}
          />
        )}
      </section>

      {/* Unknown changes (the list could not be read) leave publishing to each prompt's editor. */}
      {changes != null && (
        <PublishBar
          count={changes.length}
          what={`the prompts for ${scopeName}`}
          onReview={() => setPublishOpen(true)}
          testId="prompts-publish-bar"
        />
      )}
      <PublishDialog
        opened={publishOpen}
        onClose={() => setPublishOpen(false)}
        title="Publish prompts"
        changes={changeItems}
        onPublish={handlePublishAll}
        busy={publishing}
        confirmLabel={changeItems.length > 1 ? `Publish ${changeItems.length} prompts` : 'Publish'}
        confirmTestId="prompts-publish-confirm"
      />
    </div>
  )
}

/** Field-level map of what this prompt injects from the Taxonomy. */
function InjectionPanel({ injects }: { injects: PromptInject[] }) {
  return (
    <div className="prompt-injects">
      <div className="prompt-injects-title">Injects from the taxonomy</div>
      {injects.map((inj) => (
        <div key={inj.placeholder} className="prompt-injects-row">
          <code className="prompt-injects-placeholder">{`{${inj.placeholder}}`}</code>
          <div className="prompt-injects-detail">
            <div className="prompt-injects-label">{inj.label}</div>
            <div className="prompt-injects-fields">
              {inj.fields.map((f) => (
                <span key={f} className="prompt-injects-field">{f}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
      <Link
        to="/organization/admin/taxonomy"
        search={(prev) => prev}
        className="prompt-injects-link"
      >
        View in Taxonomy <ArrowRight size={13} />
      </Link>
    </div>
  )
}
